import React, { useState } from "react";
import { Menu } from "@headlessui/react";
import { useDispatch, useSelector } from "react-redux";
import {
  PencilIcon,
  TrashIcon,
  EyeIcon,
  ChevronLeftIcon,
  RefreshIcon,
} from "@heroicons/react/outline";

import ConfirmDialog from "components/lib/Shared/ConfirmDialog";
import TableActionMenuWrapper from "./TableActionMenuWrapper";
import { closeModal, openModal } from "state/slices/modals";
import DownloadFileIcon from "./Icons/DownloadFileIcon";
import EmailIcon from "./Icons/EmailIcon";
import { Formik, Form, Field } from "formik";
import _isEmpty from "lodash/isEmpty";
import { downloadBusinessDocumentsAsync } from "state/slices/company";
import { getBusinessDocumentData } from "config/documentData";
import { useToast } from "hooks/useToast";
import CessationIcon from "./Icons/CessationIcon";
import ChangeOfParticularsIcon from "./Icons/ChangeOfParticularsIcon";

const TableActionMenu = ({
  row,
  documentsToDownload,
  disableEditBtn,
  disableDeleteBtn,
  disableReviewBtn,
  hideEditBtn,
  hideDeleteBtn,
  showViewBtn,
  showCloseBtn,
  showCorrectedBtn,
  showRecallBtn,
  showFileDownloadBtn,
  showVoid,
  children,
  showEmailReminder,
  showDeleteConfirmation = true,
  deleteConfirmationLabel = "Are you sure you want to permanently delete this entry?",
  showReviewBtn = false,
  showResendInviteBtn,
  renderViewAsSubRow = false,
  viewWithoutSubRow = false,
  showChange = false,
  showResignation = false,
  showRemoval = false,
  showRetirement = false,
  showRotation = false,
  showDeath = false,
  showBankruptcy = false,
  showMentalIncapacity = false,
  showCourtOrder = false,
  isChangesList = false,
  showShareTransfer = false,
  showTransmissionOfShares = false,
  showForfeitureOfShares = false,
  showSurrenderOfShares = false,
  showSharesBuyBack = false,
  showCancellationOfTreasuryShares = false,
  showSaleTransferOfTreasuryShares = false,
  showSharesRedemption = false,
  showNoticeToCourt = false,
  showTakeOverOffer = false,
  showManualAllot = false,
  showAdjustBODistribution = false,
  showAppointAsDirector = false,
  showCeaseAsDirector = false,
  shareTransferButtonLabel = "Share Transfer",

  handleShareTransfer = () => null,
  handleTransmissionOfShares = () => null,
  handleForfeitureOfShares = () => null,
  handleSurrenderOfShares = () => null,
  handleSharesBuyBack = () => null,
  handleCancellationOfTreasuryShares = () => null,
  handleSaleTransferOfTreasuryShares = () => null,
  handleSharesRedemption = () => null,
  handleNoticeToCourt = () => null,
  handleTakeOverOffer = () => null,
  handleManualAllot = () => null,
  handleAdjustBODistribution = () => null,
  handleChange = () => null,
  handleAppointAsDirector = () => null,
  handleCeaseAsDirector = () => null,
  handleResignation = () => null,
  handleRemoval = () => null,
  handleRetirement = () => null,
  handleRotation = () => null,
  handleDeath = () => null,
  handleBankruptcy = () => null,
  handleMentalIncapacity = () => null,
  handleCourtOrder = () => null,
  handleEdit = () => null,
  handleDelete = () => null,
  handleView = () => null,
  handleCorrected = () => null,
  handleRecall = () => null,
  handleReview = () => null,
  handleResendInvite = () => null,
  handleFileDownload = () => null,
  handleEmailReminder = () => null,
  handleVoid = () => null,
  handleClose = () => null,
}) => {
  const dispatch = useDispatch();
  const { modalRow } = useSelector((state) => state.modalsSlice);
  const [isDownloading, setIsDownloading] = useState(false);
  const { toast } = useToast();
  let reviewBtnProps = {};
  if (row?.getToggleRowExpandedProps) {
    reviewBtnProps = row?.getToggleRowExpandedProps();
  }

  const originalRow = row.original;

  const renderReviewBtn =
    showReviewBtn &&
    originalRow.type &&
    originalRow?.type === "invited" &&
    originalRow?.inviteStatus === "Data Pending Review";

  return (
    <div>
      <TableActionMenuWrapper>
        {showResendInviteBtn && (
          <Menu.Item>
            {({ active }) => (
              <button
                type="button"
                onClick={() => handleResendInvite(originalRow)}
                className={`${
                  active ? "bg-violet-500 text-white" : "text-gray-900"
                } group flex rounded-md items-center w-full px-2 py-2 text-sm disabled:cursor-not-allowed disabled:text-gray-400`}
              >
                Resend invite
              </button>
            )}
          </Menu.Item>
        )}
        {showCloseBtn && (
          <Menu.Item>
            {({ active }) => (
              <button
                type="button"
                onClick={() => handleClose(row)}
                className={`${
                  active ? "bg-violet-500 text-white" : "text-gray-900"
                } group flex rounded-md items-center w-full px-2 py-2 text-sm disabled:cursor-not-allowed disabled:text-gray-400`}
              >
                <>Close</>
              </button>
            )}
          </Menu.Item>
        )}
        {viewWithoutSubRow && showViewBtn && (
          <Menu.Item>
            {({ active }) => (
              <button
                type="button"
                onClick={() => handleView({ ...row, ...originalRow })}
                className={`${
                  active ? "bg-violet-500 text-white" : "text-gray-900"
                } group flex rounded-md items-center w-full px-2 py-2 text-sm disabled:cursor-not-allowed disabled:text-gray-400`}
              >
                <>
                  <EyeIcon className="w-5 h-5 mr-2" aria-hidden="true" />
                  View
                </>
              </button>
            )}
          </Menu.Item>
        )}
        {!viewWithoutSubRow &&
          showViewBtn &&
          (row.isExpanded ? !renderReviewBtn : true) && (
            <Menu.Item>
              {({ active }) => (
                <button
                  type="button"
                  onClick={(e) => {
                    handleView({ ...row, ...originalRow });
                    if (renderViewAsSubRow) reviewBtnProps?.onClick?.(e);
                  }}
                  className={`${
                    active ? "bg-violet-500 text-white" : "text-gray-900"
                  } group flex rounded-md items-center w-full px-2 py-2 text-sm disabled:cursor-not-allowed disabled:text-gray-400`}
                >
                  {row.isExpanded ? (
                    "Close"
                  ) : (
                    <>
                      <EyeIcon className="w-5 h-5 mr-2" aria-hidden="true" />
                      View
                    </>
                  )}
                </button>
              )}
            </Menu.Item>
          )}

        {showVoid && (
          <Menu.Item>
            {({ active }) => (
              <button
                type="button"
                onClick={() => handleVoid()}
                className={`${
                  active ? "bg-violet-500 text-white" : "text-gray-900"
                } group flex gap-1 rounded-md items-center w-full px-2 py-2 text-sm disabled:cursor-not-allowed disabled:text-gray-400`}
              >
                <ChevronLeftIcon className="w-5 h-5 mr-2" aria-hidden="true" />
                Void
              </button>
            )}
          </Menu.Item>
        )}

        {showEmailReminder && (
          <Menu.Item>
            {({ active }) => (
              <button
                type="button"
                onClick={() => handleEmailReminder()}
                className={`${
                  active ? "bg-violet-500 text-white" : "text-gray-900"
                } group flex gap-1 rounded-md items-center w-full px-2 py-2 text-sm disabled:cursor-not-allowed disabled:text-gray-400`}
              >
                <EmailIcon className="w-5 h-5 mr-2" />
                Email reminder
              </button>
            )}
          </Menu.Item>
        )}
        {showCorrectedBtn && (row.isExpanded ? !renderReviewBtn : true) && (
          <Menu.Item>
            {({ active }) => (
              <button
                type="button"
                onClick={(e) => {
                  handleCorrected(originalRow);
                  if (renderViewAsSubRow) reviewBtnProps?.onClick?.(e);
                }}
                className={`${
                  active ? "bg-violet-500 text-white" : "text-gray-900"
                } group flex rounded-md items-center w-full px-2 py-2 text-sm disabled:cursor-not-allowed disabled:text-gray-400`}
              >
                {row.isExpanded ? (
                  "Close"
                ) : (
                  <>
                    <PencilIcon className="w-5 h-5 mr-2" aria-hidden="true" />
                    Correct
                  </>
                )}
              </button>
            )}
          </Menu.Item>
        )}
        {!hideEditBtn && (
          <Menu.Item>
            {({ active }) => (
              <button
                type="button"
                onClick={() => handleEdit({ ...row, ...originalRow })}
                disabled={disableEditBtn}
                className={`${
                  active && !disableEditBtn
                    ? "bg-violet-500 text-white"
                    : "text-gray-900"
                } group flex rounded-md items-center w-full px-2 py-2 text-sm disabled:cursor-not-allowed disabled:text-gray-400`}
              >
                <PencilIcon className="w-5 h-5 mr-2" aria-hidden="true" />
                Edit
              </button>
            )}
          </Menu.Item>
        )}
        {showShareTransfer && (
          <Menu.Item>
            {({ active }) => (
              <button
                type="button"
                onClick={() => handleShareTransfer(row)}
                disabled={disableEditBtn}
                className={`${
                  active && !disableEditBtn
                    ? "bg-violet-500 text-white"
                    : "text-gray-900"
                } group flex rounded-md items-center w-full px-2 py-2 text-sm disabled:cursor-not-allowed disabled:text-gray-400`}
              >
                <ChangeOfParticularsIcon />
                {shareTransferButtonLabel}
              </button>
            )}
          </Menu.Item>
        )}
        {showTransmissionOfShares && (
          <Menu.Item>
            {({ active }) => (
              <button
                type="button"
                onClick={() => handleTransmissionOfShares(row)}
                disabled={disableEditBtn}
                className={`${
                  active && !disableEditBtn
                    ? "bg-violet-500 text-white"
                    : "text-gray-900"
                } group flex rounded-md items-center w-full px-2 py-2 text-sm disabled:cursor-not-allowed disabled:text-gray-400`}
              >
                <ChangeOfParticularsIcon />
                Transmission Of Shares
              </button>
            )}
          </Menu.Item>
        )}
        {showForfeitureOfShares && (
          <Menu.Item>
            {({ active }) => (
              <button
                type="button"
                onClick={() => handleForfeitureOfShares(row)}
                disabled={disableEditBtn}
                className={`${
                  active && !disableEditBtn
                    ? "bg-violet-500 text-white"
                    : "text-gray-900"
                } group flex rounded-md items-center w-full px-2 py-2 text-sm disabled:cursor-not-allowed disabled:text-gray-400`}
              >
                <ChangeOfParticularsIcon />
                Forfeiture of Shares
              </button>
            )}
          </Menu.Item>
        )}
        {showSurrenderOfShares && (
          <Menu.Item>
            {({ active }) => (
              <button
                type="button"
                onClick={() => handleSurrenderOfShares(row)}
                disabled={disableEditBtn}
                className={`${
                  active && !disableEditBtn
                    ? "bg-violet-500 text-white"
                    : "text-gray-900"
                } group flex rounded-md items-center w-full px-2 py-2 text-sm disabled:cursor-not-allowed disabled:text-gray-400`}
              >
                <ChangeOfParticularsIcon />
                Surrender of shares
              </button>
            )}
          </Menu.Item>
        )}
        {showSharesBuyBack && (
          <Menu.Item>
            {({ active }) => (
              <button
                type="button"
                onClick={() => handleSharesBuyBack(row)}
                disabled={disableEditBtn}
                className={`${
                  active && !disableEditBtn
                    ? "bg-violet-500 text-white"
                    : "text-gray-900"
                } group flex rounded-md items-center w-full px-2 py-2 text-sm disabled:cursor-not-allowed disabled:text-gray-400`}
              >
                <ChangeOfParticularsIcon />
                Shares Buyback
              </button>
            )}
          </Menu.Item>
        )}
        {showCancellationOfTreasuryShares && (
          <Menu.Item>
            {({ active }) => (
              <button
                type="button"
                onClick={() => handleCancellationOfTreasuryShares(row)}
                disabled={disableEditBtn}
                className={`${
                  active && !disableEditBtn
                    ? "bg-violet-500 text-white"
                    : "text-gray-900"
                } group flex rounded-md items-center w-full px-2 py-2 text-sm disabled:cursor-not-allowed disabled:text-gray-400`}
              >
                <ChangeOfParticularsIcon />
                Cancellation of Treasury Shares
              </button>
            )}
          </Menu.Item>
        )}
        {showSaleTransferOfTreasuryShares && (
          <Menu.Item>
            {({ active }) => (
              <button
                type="button"
                onClick={() => handleSaleTransferOfTreasuryShares(row)}
                disabled={disableEditBtn}
                className={`${
                  active && !disableEditBtn
                    ? "bg-violet-500 text-white"
                    : "text-gray-900"
                } group flex rounded-md items-center w-full px-2 py-2 text-sm disabled:cursor-not-allowed disabled:text-gray-400`}
              >
                <ChangeOfParticularsIcon />
                Sale / Transfer of Treasury Shares
              </button>
            )}
          </Menu.Item>
        )}
        {showSharesRedemption && (
          <Menu.Item>
            {({ active }) => (
              <button
                type="button"
                onClick={() => handleSharesRedemption(row)}
                disabled={disableEditBtn}
                className={`${
                  active && !disableEditBtn
                    ? "bg-violet-500 text-white"
                    : "text-gray-900"
                } group flex rounded-md items-center w-full px-2 py-2 text-sm disabled:cursor-not-allowed disabled:text-gray-400`}
              >
                <ChangeOfParticularsIcon />
                Shares Redemption
              </button>
            )}
          </Menu.Item>
        )}
        {showNoticeToCourt && (
          <Menu.Item>
            {({ active }) => (
              <button
                type="button"
                onClick={() => handleNoticeToCourt(row)}
                disabled={disableEditBtn}
                className={`${
                  active && !disableEditBtn
                    ? "bg-violet-500 text-white"
                    : "text-gray-900"
                } group flex rounded-md items-center w-full px-2 py-2 text-sm disabled:cursor-not-allowed disabled:text-gray-400`}
              >
                <ChangeOfParticularsIcon />
                Notice to Court to cancel Special Resolution Shares Redemption
              </button>
            )}
          </Menu.Item>
        )}
        {showTakeOverOffer && (
          <Menu.Item>
            {({ active }) => (
              <button
                type="button"
                onClick={() => handleTakeOverOffer(row)}
                disabled={disableEditBtn}
                className={`${
                  active && !disableEditBtn
                    ? "bg-violet-500 text-white"
                    : "text-gray-900"
                } group flex rounded-md items-center w-full px-2 py-2 text-sm disabled:cursor-not-allowed disabled:text-gray-400`}
              >
                <ChangeOfParticularsIcon />
                Take over Offer
              </button>
            )}
          </Menu.Item>
        )}
        {showManualAllot && (
          <Menu.Item>
            {({ active }) => (
              <button
                type="button"
                onClick={() => handleManualAllot(row)}
                disabled={disableEditBtn}
                className={`${
                  active && !disableEditBtn
                    ? "bg-violet-500 text-white"
                    : "text-gray-900"
                } group flex rounded-md items-center w-full px-2 py-2 text-sm disabled:cursor-not-allowed disabled:text-gray-400`}
              >
                <ChangeOfParticularsIcon />
                Allot Manually
              </button>
            )}
          </Menu.Item>
        )}
        {showAdjustBODistribution && (
          <Menu.Item>
            {({ active }) => (
              <button
                type="button"
                onClick={() => handleAdjustBODistribution(row)}
                disabled={disableEditBtn}
                className={`${
                  active && !disableEditBtn
                    ? "bg-violet-500 text-white"
                    : "text-gray-900"
                } group flex rounded-md items-center w-full px-2 py-2 text-sm disabled:cursor-not-allowed disabled:text-gray-400`}
              >
                <ChangeOfParticularsIcon />
                Adjust BO Distribution
              </button>
            )}
          </Menu.Item>
        )}
        {showAppointAsDirector && (
          <Menu.Item>
            {({ active }) => (
              <button
                type="button"
                onClick={() => handleAppointAsDirector(row)}
                disabled={disableEditBtn}
                className={`${
                  active && !disableEditBtn
                    ? "bg-violet-500 text-white"
                    : "text-gray-900"
                } group flex rounded-md items-center w-full px-2 py-2 text-sm disabled:cursor-not-allowed disabled:text-gray-400`}
              >
                <ChangeOfParticularsIcon />
                Appoint as Director
              </button>
            )}
          </Menu.Item>
        )}
        {showCeaseAsDirector && (
          <Menu.Item>
            {({ active }) => (
              <button
                type="button"
                onClick={() => handleCeaseAsDirector(row)}
                disabled={disableEditBtn}
                className={`${
                  active && !disableEditBtn
                    ? "bg-violet-500 text-white"
                    : "text-gray-900"
                } group flex rounded-md items-center w-full px-2 py-2 text-sm disabled:cursor-not-allowed disabled:text-gray-400`}
              >
                <ChangeOfParticularsIcon />
                Cease as Director
              </button>
            )}
          </Menu.Item>
        )}
        {showChange && (
          <Menu.Item>
            {({ active }) => (
              <button
                type="button"
                onClick={() => handleChange(row)}
                disabled={disableEditBtn}
                className={`${
                  active && !disableEditBtn
                    ? "bg-violet-500 text-white"
                    : "text-gray-900"
                } group flex rounded-md items-center w-full px-2 py-2 text-sm disabled:cursor-not-allowed disabled:text-gray-400`}
              >
                <ChangeOfParticularsIcon />
                Change of Particulars
              </button>
            )}
          </Menu.Item>
        )}
        {showResignation && (
          <Menu.Item>
            {({ active }) => (
              <button
                type="button"
                onClick={() => handleResignation(row)}
                disabled={disableEditBtn}
                className={`${
                  active && !disableEditBtn
                    ? "bg-red-600 text-white"
                    : "text-gray-900"
                } group flex rounded-md items-center w-full px-2 py-2 text-sm disabled:cursor-not-allowed disabled:text-gray-400`}
              >
                <CessationIcon className="mr-2" />
                Resignation
              </button>
            )}
          </Menu.Item>
        )}
        {showRemoval && (
          <Menu.Item>
            {({ active }) => (
              <button
                type="button"
                onClick={() => handleRemoval(row)}
                disabled={disableEditBtn}
                className={`${
                  active && !disableEditBtn
                    ? "bg-red-600 text-white"
                    : "text-gray-900"
                } group flex rounded-md items-center w-full px-2 py-2 text-sm disabled:cursor-not-allowed disabled:text-gray-400`}
              >
                <CessationIcon className="mr-2" />
                Removal
              </button>
            )}
          </Menu.Item>
        )}
        {showRetirement && (
          <Menu.Item>
            {({ active }) => (
              <button
                type="button"
                onClick={() => handleRetirement(row)}
                disabled={disableEditBtn}
                className={`${
                  active && !disableEditBtn
                    ? "bg-red-600 text-white"
                    : "text-gray-900"
                } group flex rounded-md items-center w-full px-2 py-2 text-sm disabled:cursor-not-allowed disabled:text-gray-400`}
              >
                <CessationIcon className="mr-2" />
                Retirement
              </button>
            )}
          </Menu.Item>
        )}
        {showRotation && (
          <Menu.Item>
            {({ active }) => (
              <button
                type="button"
                onClick={() => handleRotation(row)}
                disabled={disableEditBtn}
                className={`${
                  active && !disableEditBtn
                    ? "bg-red-600 text-white"
                    : "text-gray-900"
                } group flex rounded-md items-center w-full px-2 py-2 text-sm disabled:cursor-not-allowed disabled:text-gray-400`}
              >
                <CessationIcon className="mr-2" />
                Rotation
              </button>
            )}
          </Menu.Item>
        )}
        {showDeath && (
          <Menu.Item>
            {({ active }) => (
              <button
                type="button"
                onClick={() => handleDeath(row)}
                disabled={disableEditBtn}
                className={`${
                  active && !disableEditBtn
                    ? "bg-red-600 text-white"
                    : "text-gray-900"
                } group flex rounded-md items-center w-full px-2 py-2 text-sm disabled:cursor-not-allowed disabled:text-gray-400`}
              >
                <CessationIcon className="mr-2" />
                Death
              </button>
            )}
          </Menu.Item>
        )}
        {showBankruptcy && (
          <Menu.Item>
            {({ active }) => (
              <button
                type="button"
                onClick={() => handleBankruptcy(row)}
                disabled={disableEditBtn}
                className={`${
                  active && !disableEditBtn
                    ? "bg-red-600 text-white"
                    : "text-gray-900"
                } group flex rounded-md items-center w-full px-2 py-2 text-sm disabled:cursor-not-allowed disabled:text-gray-400`}
              >
                <CessationIcon className="mr-2" />
                Bankruptcy
              </button>
            )}
          </Menu.Item>
        )}
        {showMentalIncapacity && (
          <Menu.Item>
            {({ active }) => (
              <button
                type="button"
                onClick={() => handleMentalIncapacity(row)}
                disabled={disableEditBtn}
                className={`${
                  active && !disableEditBtn
                    ? "bg-red-600 text-white"
                    : "text-gray-900"
                } group flex rounded-md items-center w-full px-2 py-2 text-sm disabled:cursor-not-allowed disabled:text-gray-400`}
              >
                <CessationIcon className="mr-2" />
                Mental Incapacity
              </button>
            )}
          </Menu.Item>
        )}
        {showCourtOrder && (
          <Menu.Item>
            {({ active }) => (
              <button
                type="button"
                onClick={() => handleCourtOrder(row)}
                disabled={disableEditBtn}
                className={`${
                  active && !disableEditBtn
                    ? "bg-red-600 text-white"
                    : "text-gray-900"
                } group flex rounded-md items-center w-full px-2 py-2 text-sm disabled:cursor-not-allowed disabled:text-gray-400`}
              >
                <CessationIcon className="mr-2" />
                Court Order
              </button>
            )}
          </Menu.Item>
        )}

        {showReviewBtn && !renderReviewBtn && (
          <Menu.Item>
            {({ active }) => (
              <button
                type="button"
                onClick={(e) => {
                  handleReview({ ...row, ...originalRow });
                }}
                disabled={disableReviewBtn}
                className={`${
                  active ? "bg-violet-500 text-white" : "text-gray-900"
                } group flex rounded-md items-center w-full px-2 py-2 text-sm disabled:cursor-not-allowed disabled:text-gray-400`}
              >
                <PencilIcon className="w-5 h-5 mr-2" aria-hidden="true" />
                Review
              </button>
            )}
          </Menu.Item>
        )}

        {renderReviewBtn && (
          <Menu.Item>
            {({ active }) => (
              <button
                type="button"
                onClick={(e) => {
                  handleReview(originalRow);
                  reviewBtnProps?.onClick?.(e);
                }}
                disabled={disableReviewBtn}
                className={`${
                  active ? "bg-violet-500 text-white" : "text-gray-900"
                } group flex rounded-md items-center w-full px-2 py-2 text-sm disabled:cursor-not-allowed disabled:text-gray-400`}
              >
                {!row.isExpanded && (
                  <PencilIcon className="w-5 h-5 mr-2" aria-hidden="true" />
                )}
                {row.isExpanded ? "Close" : "Review"}
              </button>
            )}
          </Menu.Item>
        )}
        {showRecallBtn && (
          <Menu.Item>
            {({ active }) => (
              <button
                type="button"
                onClick={() => handleRecall({ ...row, ...originalRow })}
                className={`${
                  active ? "bg-violet-500 text-white" : "text-gray-900"
                } group flex rounded-md items-center w-full px-2 py-2 text-sm disabled:cursor-not-allowed disabled:text-gray-400`}
              >
                <RefreshIcon className="w-5 h-5 mr-2" aria-hidden="true" />
                Recall
              </button>
            )}
          </Menu.Item>
        )}
        {!hideDeleteBtn && (
          <Menu.Item>
            {({ active }) => (
              <button
                type="button"
                onClick={() =>
                  showDeleteConfirmation
                    ? dispatch(
                        openModal({
                          modalName: "delete_confirmation_modal",
                          modalRow: originalRow,
                        })
                      )
                    : handleDelete(originalRow)
                }
                disabled={disableDeleteBtn}
                className={`${
                  active && !disableDeleteBtn
                    ? "bg-red-600 text-white"
                    : "text-gray-900"
                } group flex rounded-md items-center w-full px-2 py-2 text-sm disabled:cursor-not-allowed disabled:text-gray-400`}
              >
                <TrashIcon className="w-5 h-5 mr-2" aria-hidden="true" />
                Delete
              </button>
            )}
          </Menu.Item>
        )}
        {}
        {showFileDownloadBtn && (
          <Menu.Item>
            {({ active }) => (
              <div
                className={`${
                  active ? "bg-violet-500 text-white" : "text-gray-900"
                } group flex rounded-md items-center w-full px-2 py-2 text-sm disabled:cursor-not-allowed disabled:text-gray-400`}
              >
                <Formik
                  enableReinitialize
                  initialValues={{
                    fileUploads: documentsToDownload,
                  }}
                >
                  {() => {
                    const handleDownloadDocuments = async () => {
                      try {
                        setIsDownloading(true);
                        const businessDocuments = [];

                        getBusinessDocumentData().map((businessDocument) => {
                          const fileKey =
                            documentsToDownload[businessDocument.name];
                          if (fileKey) {
                            businessDocuments.push({
                              key: fileKey,
                              name: `${businessDocument.label}.${fileKey
                                .split(".")
                                .pop()}`,
                            });
                          }
                        });

                        await Promise.all(
                          businessDocuments.map(async (businessDocument) => {
                            return dispatch(
                              downloadBusinessDocumentsAsync({
                                companyId: row.original._id,
                                businessDocument,
                              })
                            );
                          })
                        );
                      } catch (error) {
                        toast(
                          "error",
                          error?.message ??
                            "Something went wrong, please try again"
                        );
                      } finally {
                        setIsDownloading(false);
                      }
                    };

                    return (
                      <Form className="flex-grow">
                        {!_isEmpty(documentsToDownload) ? (
                          <div>
                            <div className="flex">
                              <button
                                type="button"
                                onClick={handleDownloadDocuments}
                                className={` group flex gap-1`}
                              >
                                <DownloadFileIcon className="w-5 h-5 mr-2" />
                                Download PDF
                              </button>
                            </div>
                            {/* <div>
                              <Field
                                name="fileUploads"
                                component={FileUploadForm}
                                documentData={getBusinessDocumentData()}
                                onlyPreview
                                showDocumentLabel
                              />
                            </div> */}
                          </div>
                        ) : (
                          "No documents found"
                        )}
                      </Form>
                    );
                  }}
                </Formik>
              </div>
            )}
          </Menu.Item>
        )}
        {children}
      </TableActionMenuWrapper>
      <ConfirmDialog
        name="delete_confirmation_modal"
        onClose={() => dispatch(closeModal("delete_confirmation_modal"))}
        onConfirm={() => handleDelete(modalRow)}
      >
        {deleteConfirmationLabel}
      </ConfirmDialog>
    </div>
  );
};

export default TableActionMenu;
